import { useState } from "react";
import { Link } from "react-router-dom";
import { Check, Phone, ArrowRight, Layers, ShieldCheck, Sun, Compass, X, ChevronLeft, ChevronRight } from "lucide-react";
import { CONTACT_INFO, IMAGES, GALLERIES } from "../data";
import Meta from "../components/Meta";
import { motion, AnimatePresence } from "framer-motion";
import { fadeIn, fadeInSlow, slideInLeft, slideInRight, staggerContainer, staggerItem } from "../lib/animations";

export default function Terrasse() {
  const [activePhotoIdx, setActivePhotoIdx] = useState(null);
  const galleryItems = GALLERIES.terrasse;

  const openLightbox = (idx) => {
    setActivePhotoIdx(idx);
  };

  const closeLightbox = () => {
    setActivePhotoIdx(null);
  };

  const nextPhoto = (e) => {
    e.stopPropagation();
    setActivePhotoIdx((prev) => (prev + 1) % galleryItems.length);
  };

  const prevPhoto = (e) => {
    e.stopPropagation();
    setActivePhotoIdx((prev) => (prev - 1 + galleryItems.length) % galleryItems.length);
  };
  const materials = [
    {
      name: "Cedertræ",
      desc: "Et smukt og let materiale med naturlig modstandsdygtighed over for råd og svamp. Cedertræ patinerer til en flot sølvgrå farve, hvis det efterlades ubehandlet.",
    },
    {
      name: "Ipe & Hårdttræ",
      desc: "Eksklusive og ekstremt slidstærke træsorter med en meget høj densitet. En hårdttræsterrasse har en levetid på op til 30-40 år og føles massiv og luksuriøs.",
    },
    {
      name: "Thermo-wood",
      desc: "Varmebehandlet fyr eller gran, som er helt fri for kemikalier. Behandlingen ændrer træets cellestruktur, hvilket giver fantastisk formstabilitet og en smuk, mørkebrun glød.",
    },
    {
      name: "Klassisk fyr (Trykimprægneret)",
      desc: "Den mest økonomiske løsning, som giver en solid og funktionel terrasse. Med den rette pleje og oliebehandling kan en trykimprægneret terrasse holde i mange år.",
    },
  ];

  return (
    <>
      <Meta
        title="Terrasse"
        description="Få bygget en eksklusiv træterrasse i Helsingør og Nordsjælland. Vi rådgiver om materialer (ceder, Ipe, thermo) og bygger med skjulte skruer."
      />

      {/* Page Header Banner */}
      <section className="relative pt-40 pb-24 bg-zinc-950 text-white rounded-none overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src={IMAGES.terrasse}
            alt="Eksklusiv træterrasse i Nordsjælland"
            className="w-full h-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-zinc-950 via-zinc-950/40 to-zinc-950" />
        </div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="relative z-10 max-w-7xl mx-auto px-6 md:px-12"
        >
          <span className="text-xs font-bold uppercase tracking-[0.25em] text-amber-500 block mb-3">
            Uderum & Træterrasser
          </span>
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-light uppercase tracking-wide text-stone-50">
            Eksklusive <span className="font-semibold text-amber-500">træterrasser</span>
          </h1>
          <p className="mt-4 text-stone-300 max-w-2xl font-light leading-relaxed text-sm sm:text-base">
            Vi skaber dit nye uderum med fokus på kompromisløs finish, skjult skruemontage og et solidt underlag, der holder i årtier.
          </p>
        </motion.div>
      </section>

      {/* Main Content Section */}
      <section className="bg-stone-50 py-24 px-6 md:px-12 text-zinc-900 rounded-none overflow-hidden">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-16">
          {/* Left Column: Detailed Copy */}
          <motion.div
            variants={slideInLeft}
            initial="initial"
            whileInView="whileInView"
            viewport={{ once: true, margin: "-100px" }}
            className="lg:col-span-7 space-y-12"
          >
            <div className="space-y-6">
              <h2 className="text-2xl md:text-3xl font-semibold uppercase tracking-wide text-zinc-900">
                Skræddersyet uderum tilpasset din have og bolig
              </h2>
              <p className="text-stone-600 leading-relaxed font-light">
                En træterrasse er ikke bare en udvidelse af huset; det er rammen om dine sommeraftener, familiens grillfester og rolige stunder i solen. Hos Nordsjællands Tømrer og Snedker har vi specialiseret os i at designe og opbygge træterrasser af absolut højeste kvalitet.
              </p>
              <p className="text-stone-600 leading-relaxed font-light">
                Nøglen til en holdbar terrasse ligger i det usynlige arbejde: underkonstruktionen. Vi sikrer altid, at stolper, strøer og bjælker dimensioneres korrekt, graves til frostfri dybde og ventileres optimalt, så træet kan ånde og ikke suger fugt.
              </p>
            </div>

            {/* Premium details */}
            <div className="bg-white border border-stone-200 p-8 rounded-none space-y-6">
              <h3 className="text-lg font-bold uppercase tracking-wide text-zinc-900 flex items-center gap-2">
                <Layers className="w-5 h-5 text-amber-600" />
                <span>Premium finish med skjult montage</span>
              </h3>
              <p className="text-stone-600 text-sm leading-relaxed font-light">
                Vi tilbyder opbygning med **skjult skruemontage** (f.eks. ved brug af Camo-systemet eller specielle clips). Dette betyder, at der ikke er synlige skruehuller på terrassens overflade. Det giver et rent og eksklusivt udtryk, fjerner risikoen for splinter omkring skruerne og forlænger bræddernes levetid, da der ikke trænger vand ned i skruehullerne.
              </p>
            </div>

            {/* Materials Breakdown */}
            <div className="space-y-6">
              <h3 className="text-xl font-bold uppercase tracking-wide text-zinc-900">
                Hvilken træsort skal du vælge?
              </h3>
              <motion.div
                variants={staggerContainer}
                initial="initial"
                whileInView="whileInView"
                viewport={{ once: true, margin: "-100px" }}
                className="grid grid-cols-1 sm:grid-cols-2 gap-6"
              >
                {materials.map((material, idx) => (
                  <motion.div
                    key={idx}
                    variants={staggerItem}
                    className="bg-stone-100 p-6 border border-stone-200/60 rounded-none space-y-2"
                  >
                    <h4 className="text-sm font-bold uppercase tracking-wider text-zinc-900">
                      {material.name}
                    </h4>
                    <p className="text-stone-600 text-xs leading-relaxed font-light">
                      {material.desc}
                    </p>
                  </motion.div>
                ))}
              </motion.div>
            </div>
          </motion.div>

          {/* Right Column: Sidebar & Highlights */}
          <motion.div
            variants={slideInRight}
            initial="initial"
            whileInView="whileInView"
            viewport={{ once: true, margin: "-100px" }}
            className="lg:col-span-5 space-y-8"
          >
            {/* Highlight Box */}
            <div className="bg-zinc-900 text-white p-8 border border-zinc-800 rounded-none space-y-6">
              <h3 className="text-lg font-bold uppercase tracking-wider text-stone-100 border-b border-zinc-800 pb-3">
                Vores terrasse-koncept
              </h3>
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <div className="p-1.5 bg-white text-amber-900 rounded-none shrink-0 mt-0.5">
                    <ShieldCheck className="w-4 h-4" />
                  </div>
                  <div>
                    <h4 className="text-xs font-bold uppercase tracking-wider text-stone-200">Solidt fundament</h4>
                    <p className="text-[11px] text-stone-400 leading-relaxed font-light mt-1">
                      Stolper graves til frostfri dybde eller monteres på skruefundamenter for maksimal stabilitet.
                    </p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <div className="p-1.5 bg-white text-amber-900 rounded-none shrink-0 mt-0.5">
                    <Sun className="w-4 h-4" />
                  </div>
                  <div>
                    <h4 className="text-xs font-bold uppercase tracking-wider text-stone-200">Optimal soludnyttelse</h4>
                    <p className="text-[11px] text-stone-400 leading-relaxed font-light mt-1">
                      Vi rådgiver om placering, trapper og niveauer i forhold til solens gang og havens indretning.
                    </p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <div className="p-1.5 bg-white text-amber-900 rounded-none shrink-0 mt-0.5">
                    <Compass className="w-4 h-4" />
                  </div>
                  <div>
                    <h4 className="text-xs font-bold uppercase tracking-wider text-stone-200">Millimeterpræcision</h4>
                    <p className="text-[11px] text-stone-400 leading-relaxed font-light mt-1">
                      Skarpe geringssnit, perfekte kurver og snorlige rækker kendetegner vores arbejde.
                    </p>
                  </div>
                </li>
              </ul>
            </div>

            {/* Quick Contact Card */}
            <div className="bg-amber-500 text-white p-8 rounded-none space-y-4">
              <h3 className="text-lg font-bold uppercase tracking-wider text-stone-100">
                Drømmer du om en ny terrasse?
              </h3>
              <p className="text-xs text-stone-300 leading-relaxed font-light">
                Lad os komme forbi og måle op. Vi rådgiver om materialevalg og giver dig en fast pris på opbygningen.
              </p>
              <div className="pt-4 flex flex-col gap-3">
                <a
                  href={`tel:${CONTACT_INFO.phone}`}
                  className="w-full py-3 bg-stone-50 hover:bg-stone-200 text-zinc-950 text-xs font-bold uppercase tracking-widest transition-colors duration-200 rounded-none flex items-center justify-center gap-2 whitespace-nowrap"
                >
                  <Phone className="w-3.5 h-3.5" />
                  <span>Ring {CONTACT_INFO.phoneFormatted}</span>
                </a>
                <Link
                  to="/kontakt"
                  className="w-full py-3 border border-stone-400 hover:border-amber-500 hover:text-amber-500 text-stone-200 text-xs font-bold uppercase tracking-widest transition-colors duration-200 rounded-none flex items-center justify-center gap-2"
                >
                  <span>Skriv til os</span>
                  <ArrowRight className="w-3.5 h-3.5" />
                </Link>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Gallery Section */}
      <section className="bg-zinc-950 text-white py-24 px-6 md:px-12 border-t border-zinc-800 rounded-none overflow-hidden">
        <div className="max-w-7xl mx-auto">
          <div className="text-left mb-16">
            <span className="text-xs font-bold uppercase tracking-[0.25em] text-amber-500 block mb-3">
              Vores Håndværk i Billeder
            </span>
            <h2 className="text-2xl md:text-3xl font-light uppercase tracking-wide text-stone-50">
              Galleri: <span className="font-semibold text-amber-500">Træterrasser</span>
            </h2>
            <p className="mt-4 text-stone-400 max-w-2xl font-light leading-relaxed text-sm">
              Gå på opdagelse i et udvalg af vores afsluttede terrasseprojekter i Helsingør og Nordsjælland. Hvert billede repræsenterer vores kompromisløse tilgang til kvalitet og finish.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-0 border border-zinc-800">
            {galleryItems.map((item, idx) => (
              <div
                key={idx}
                onClick={() => openLightbox(idx)}
                className="group relative h-80 overflow-hidden cursor-pointer border-r border-b last:border-r-0 lg:last:border-r-0 border-zinc-800"
              >
                <img
                  src={item.url}
                  alt={item.title}
                  className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-zinc-950 via-zinc-950/40 to-transparent opacity-90 group-hover:opacity-100 transition-opacity duration-300" />
                <div className="absolute bottom-0 left-0 right-0 p-6 transform translate-y-1 group-hover:translate-y-0 transition-transform duration-300">
                  <span className="text-xs font-bold uppercase tracking-widest text-white block">
                    {item.title}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Lightbox Modal */}
      <AnimatePresence>
        {activePhotoIdx !== null && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={closeLightbox}
            className="fixed inset-0 z-50 bg-zinc-950/95 flex items-center justify-center p-4 md:p-10"
          >
            <button
              onClick={closeLightbox}
              className="absolute top-6 right-6 text-stone-400 hover:text-white transition-colors p-2 z-50 cursor-pointer"
              aria-label="Luk galleri"
            >
              <X className="w-8 h-8" />
            </button>

            <button
              onClick={prevPhoto}
              className="absolute left-4 md:left-8 text-stone-400 hover:text-white transition-colors p-2 z-50 cursor-pointer bg-zinc-900/50 hover:bg-zinc-900"
              aria-label="Forrige billede"
            >
              <ChevronLeft className="w-8 h-8" />
            </button>

            <button
              onClick={nextPhoto}
              className="absolute right-4 md:right-8 text-stone-400 hover:text-white transition-colors p-2 z-50 cursor-pointer bg-zinc-900/50 hover:bg-zinc-900"
              aria-label="Næste billede"
            >
              <ChevronRight className="w-8 h-8" />
            </button>

            <div className="relative max-w-5xl max-h-[80vh] flex flex-col items-center" onClick={(e) => e.stopPropagation()}>
              <motion.img
                key={activePhotoIdx}
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                transition={{ duration: 0.3 }}
                src={galleryItems[activePhotoIdx].url}
                alt={galleryItems[activePhotoIdx].title}
                className="max-w-full max-h-[70vh] object-contain border border-zinc-800"
              />
              <div className="text-center mt-4 max-w-xl">
                <h3 className="text-sm font-bold uppercase tracking-wider text-amber-500">
                  {galleryItems[activePhotoIdx].title}
                </h3>
                <p className="text-xs text-stone-300 font-light mt-1">
                  {galleryItems[activePhotoIdx].desc}
                </p>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
